import { Link } from "react-router-dom";
import { FiFacebook, FiInstagram, FiLinkedin } from "react-icons/fi";
import eazyPaisaLogo from "../assets/eazypaisa.webp";
import SecurityNotice from "./SecurityNotice";

const QUICK_LINKS = [
  { label: "Loan Calculator", to: "/loan-calculator" },
  { label: "About Us", to: "/about" },
  { label: "FAQ", to: "/faq" },
  { label: "Blog", to: "/blogs" },
];

const LEGAL_LINKS = [
  { label: "Loan Details", to: "/loan-details" },
  { label: "Terms & Conditions", to: "/terms-condition" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Interest Rate Policy", to: "/interest-rate-policy" },
  { label: "Risk Management Policy", to: "/risk-management-policy" },
];

const POLICY_LINKS = [
  { label: "Cancellation & Refund Policy", to: "/cancellation-refund-policy" },
  { label: "Grievance Redressal", to: "/grievance-redressal" },
  { label: "Fair Practice Code", to: "/fair-practice-code" },
  { label: "Corporate Governance", to: "/corporate-governance" },
  { label: "KYC & AML Policy", to: "/kyc-aml-policy" },
];

// Social profile URLs come from env so each brand deployment can set its own.
const SOCIAL_LINKS = [
  { label: "LinkedIn", href: import.meta.env.VITE_SOCIAL_LINKEDIN_URL, Icon: FiLinkedin },
  { label: "Facebook", href: import.meta.env.VITE_SOCIAL_FACEBOOK_URL, Icon: FiFacebook },
  { label: "Instagram", href: import.meta.env.VITE_SOCIAL_INSTAGRAM_URL, Icon: FiInstagram },
];

const OFFICERS = [
  {
    label: "Grievance Officer",
    email: import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_EMAIL,
    phone: import.meta.env.VITE_BRAND_GRIEVANCE_OFFICER_PHONE,
  },
  {
    label: "Nodal Officer",
    email: import.meta.env.VITE_BRAND_NODAL_OFFICER_EMAIL,
    phone: import.meta.env.VITE_BRAND_NODAL_OFFICER_PHONE,
  },
].filter((officer) => officer.email || officer.phone);

const linkClass = "text-[15px] text-ep-muted transition-colors hover:text-ep-green";

const Footer = () => {
  const companyEmail = import.meta.env.VITE_COMPANY_EMAIL;
  const companyPhone = import.meta.env.VITE_COMPANY_PHONE;

  return (
    <footer>
      <SecurityNotice />

      <div className="bg-ep-ivory pb-8 pt-10 lg:pt-10">
        <div className="page-container">
          {/* Logo + tagline */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <Link to="/" aria-label="EazyPaisa home" className="shrink-0">
              <img
                className="h-20 w-auto object-contain lg:h-24"
                src={eazyPaisaLogo}
                alt="EazyPaisa"
                width={595}
                height={216}
                loading="lazy"
              />
            </Link>
            <p className="font-heading text-[26px] italic text-ep-green lg:mt-3 lg:text-[28px]">Sapno ka eazy rasta</p>
          </div>

          {/* Link columns */}
          <div className="mt-8 grid gap-10 sm:grid-cols-2 lg:mt-10 lg:grid-cols-[1.4fr_1fr_1fr_0.75fr] lg:gap-8">
            <div className="max-w-[380px]">
              <h2 className="font-serif text-[24px] font-normal text-ep-green">Contact</h2>
              <ul className="mt-4 space-y-1 text-[15px] leading-[1.6] text-ep-muted">
                {companyEmail && (
                  <li>
                    <a href={`mailto:${companyEmail}`} className="hover:text-ep-green">{companyEmail}</a>
                  </li>
                )}
                {companyPhone && (
                  <li>
                    <a href={`tel:${companyPhone}`} className="hover:text-ep-green">{companyPhone}</a>
                  </li>
                )}
                <li>24/7 Support</li>
              </ul>
              <div className="mt-4 text-[15px] leading-[1.65] text-ep-muted">
                <p>Grievance Redressal</p>
                <p>
                  For registering a complaint or understanding the grievance escalation mechanism, please visit our{" "}
                  <Link to="/grievance-redressal" className="underline-offset-4 hover:text-ep-green hover:underline">
                    Grievance Redressal page
                  </Link>
                  .
                </p>
              </div>
              {OFFICERS.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-[14px] leading-[1.5] text-ep-muted">
                  {OFFICERS.map((officer) => (
                    <div key={officer.label}>
                      <p className="font-medium text-ep-green">{officer.label}</p>
                      {officer.email && (
                        <a href={`mailto:${officer.email}`} className="block hover:text-ep-green">{officer.email}</a>
                      )}
                      {officer.phone && (
                        <a href={`tel:${officer.phone}`} className="block hover:text-ep-green">{officer.phone}</a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <nav aria-labelledby="footer-quick-links">
              <h2 id="footer-quick-links" className="font-serif text-[24px] font-normal text-ep-green">Quick Links</h2>
              <ul className="mt-4 space-y-2.5">
                {QUICK_LINKS.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </nav>

            <nav aria-labelledby="footer-legal">
              <h2 id="footer-legal" className="font-serif text-[24px] font-normal text-ep-green">Legal</h2>
              <ul className="mt-4 space-y-2.5">
                {LEGAL_LINKS.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Continuation of the legal links — no heading, aligned to the headings row */}
            <nav aria-label="Policies">
              <ul className="space-y-2.5 lg:pt-1">
                {POLICY_LINKS.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={linkClass}>{link.label}</Link>
                  </li>
                ))}
                <li>
                  <Link to="/grievance-redressal" className={linkClass}>
                    View Grievance Redressal Mechanism →
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Company info */}
          <div className="mt-8 flex flex-col gap-1 border-t border-ep-line pt-5 text-[14px] leading-[1.6] text-ep-muted lg:flex-row lg:flex-wrap lg:gap-x-8">
            <p>
              Copyright © {new Date().getFullYear()} {import.meta.env.VITE_COMPANY_NAME || "EazyPaisa"}. All rights reserved.
            </p>
            <p>NBFC Partner: {import.meta.env.VITE_COMPANY_NBFC}</p>
            <p>
              Powered By:{" "}
              <a href="https://fintechcloud.com/" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-ep-green">
                Fintech Cloud Private Limited
              </a>
            </p>
          </div>

          {/* Bottom bar */}
          <div className="mt-5 flex flex-col gap-5 border-t border-ep-line pt-5 text-[15px] text-ep-green md:flex-row md:items-center md:justify-between">
            <p>RBI NBFC License: B-14.01648</p>
            <div className="flex items-center gap-5">
              <span className="text-ep-muted">Follow us:</span>
              {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                <a
                  key={label}
                  href={href || "#"}
                  target={href ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="text-xl text-ep-green transition-opacity hover:opacity-70"
                >
                  <Icon aria-hidden />
                </a>
              ))}
            </div>
            <p>256-bit SSL</p>
            <p>Made in India 🇮🇳</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
